import { createFileRoute } from "@tanstack/react-router";
import { Download, FileText } from "lucide-react";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/downloads")({
  head: () => ({
    meta: [
      { title: "Downloads | Cartridge Pen Instructions | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Free PDF build instructions for .30 caliber and .50 BMG rifle cartridge pen assemblies — drill sizes, tube lengths and turning dimensions.",
      },
      { property: "og:title", content: "Cartridge pen build instructions (free PDFs)" },
      {
        property: "og:description",
        content: "Download Paul's .30 cal and .50 BMG cartridge pen assembly instructions.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/downloads") },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/downloads") }],
  }),
  component: Downloads,
});

const files = [
  {
    title: "30 cal instructions",
    href: "/downloads/30-cal-instructions.pdf",
    fileName: "30-cal-instructions.pdf",
    format: "PDF",
    pages: "2 pages",
    size: "69 KB",
    body: "Assembly notes for .308, .30-06 and other .30 caliber cases: base diameters, 7 mm drilling, recessed clearance holes and finished barrel dimensions.",
  },
  {
    title: "50BMG Instructions",
    href: "/downloads/50bmg-instructions.pdf",
    fileName: "50bmg-instructions.pdf",
    format: "PDF",
    pages: "5 pages",
    size: "454 KB",
    body: "Step-by-step build for the .50 BMG cartridge pen, including case preparation, tube fitting, turning sequence and final assembly.",
  },
];

function Downloads() {
  return (
    <>
      <Breadcrumbs
        trail={[{ label: "Home", to: "/" }, { label: "For Penturners", to: "/for-pen-turners" }, { label: "Downloads" }]}
      />
      <PageHeader
        eyebrow="For penturners"
        title="Downloads."
        lede="Written instructions from Paul's own bench, free to keep. Open them in the browser or save the PDF and take it out to the lathe."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask a technical question
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="dl-files-heading">
        <div className="shell">
          <SectionHeading
            id="dl-files-heading"
            eyebrow="Instruction sheets"
            title="Cartridge pen build guides."
            lede="Both files cover the dimensions turners ask about most: case base diameters, tube lengths, drill sizes and the order of assembly."
          />
          <ul className="mt-12 grid gap-px border border-hairline bg-hairline md:grid-cols-2">
            {files.map((f) => (
              <li key={f.href} className="flex flex-col bg-background p-7 sm:p-8">
                <span
                  aria-hidden="true"
                  className="inline-flex size-11 items-center justify-center rounded-sm border border-hairline text-walnut"
                >
                  <FileText className="size-5" />
                </span>
                <h3 className="mt-5 font-[family-name:var(--font-display)] text-2xl">{f.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                <p className="mt-4 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {f.format} · {f.pages} · {f.size}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a
                    href={f.href}
                    download={f.fileName}
                    className="inline-flex h-11 items-center gap-2 rounded-sm bg-ink px-5 text-sm font-semibold text-canvas motion-craft transition-opacity hover:opacity-90"
                  >
                    <Download aria-hidden="true" className="size-4" />
                    Download
                    <span className="sr-only">{f.title} (PDF)</span>
                  </a>
                  <a
                    href={f.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-walnut underline underline-offset-4"
                  >
                    View in browser
                    <span className="sr-only"> — {f.title} (PDF)</span>
                  </a>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Shared freely for personal shop use. If something in a sheet does not match the kit in front of you, send
            Paul a note — he would rather correct the file than have you scrap a blank.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Need the components to go with the instructions?"
        body="Paul stocks .30 and .50 caliber cases and hardware, plus blanks cast to order."
        label="Ask about components"
      />
    </>
  );
}
